import { useEffect, useState } from "react";
import { X, Buildings, User as UserIcon, Briefcase, FilePdf, Download } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { downloadCsv } from "@/lib/reportHelpers";
import { money } from "@/lib/money";

const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "outstanding", label: "Outstanding" },
  { key: "paid", label: "Paid" },
  { key: "overdue", label: "Overdue" },
];

export default function ReportDrillInModal({ type, id, name, onClose, dateStart, dateEnd }) {
  // type: "client" | "worker" | "project"
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr(""); setData(null);
    const url = `/reports/drilldown/${type}/${id}`;
    const params = { start: dateStart || undefined, end: dateEnd || undefined };
    if (type === "client") params.status = status;
    api.get(url, { params })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail?.toString() || e.message));
  }, [type, id, status, dateStart, dateEnd]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-start justify-center p-4 z-50 overflow-y-auto"
         data-testid="report-drilldown-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-md w-full max-w-5xl my-8 p-6 border border-slate-200 shadow-lg">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-3 rounded-md ${type === "client" ? "bg-teal-100 text-teal-700" : type === "worker" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
              {type === "client" ? <Buildings size={22} weight="duotone" />
                : type === "worker" ? <UserIcon size={22} weight="duotone" />
                : <Briefcase size={22} weight="duotone" />}
            </div>
            <div className="min-w-0">
              <div className="tinylabel">{type === "client" ? "Client detail" : type === "worker" ? "Worker detail" : "Project detail"}</div>
              <h2 className="text-2xl font-black truncate">{name}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={24} /></button>
        </div>

        {err && <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm">{err}</div>}
        {!data && !err && <div className="p-8 text-slate-400 text-center">Loading…</div>}

        {data && type === "client" && <ClientDrill data={data} status={status} setStatus={setStatus} />}
        {data && type === "worker" && <WorkerDrill data={data} />}
        {data && type === "project" && <ProjectDrill data={data} />}
      </div>
    </div>
  );
}

function ClientDrill({ data, status, setStatus }) {
  const { invoices, summary, client } = data;
  const exportCsv = () => {
    if (!invoices?.length) return;
    downloadCsv(`invoices-${(client?.name || "client").replace(/\s+/g, "_")}-${status}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "number", label: "Invoice #" },
        { key: "week_ending", label: "Week Ending" },
        { key: "sent_at", label: "Sent" },
        { key: "total", label: "Total" },
        { key: "balance_due", label: "Balance" },
        { key: "status", label: "Status" },
      ], invoices);
  };
  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniStat label="Total billed" value={money(summary.billed)} />
        <MiniStat label="Total paid" value={money(summary.paid)} tone="text-emerald-600" />
        <MiniStat label="Outstanding" value={money(summary.outstanding)} tone="text-rose-600" />
        <MiniStat label="Invoices" value={summary.count} />
      </div>

      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex border border-slate-300 rounded-md overflow-hidden bg-white">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setStatus(t.key)}
                    data-testid={`drilldown-tab-${t.key}`}
                    className={`px-4 h-9 text-sm font-semibold border-r border-slate-200 last:border-r-0 ${status === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <button data-testid="drilldown-export-csv" onClick={exportCsv} disabled={!invoices?.length}
                className="h-9 px-3 border border-slate-300 rounded-md text-sm font-semibold bg-white hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-40">
          <Download size={14} weight="bold" /> CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Invoice #</th>
              <th className="text-left px-4 py-3">Week ending</th>
              <th className="text-left px-4 py-3">Sent</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No invoices in this filter.</td></tr>}
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono font-medium">{i.number}</td>
                <td className="px-4 py-3">{shortDate(i.week_ending)}</td>
                <td className="px-4 py-3 text-slate-600">{shortDate(i.sent_at || i.created_at)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(i.total)}</td>
                <td className={`px-4 py-3 text-right font-mono ${i.balance_due > 0 ? "text-rose-600 font-bold" : "text-slate-400"}`}>{money(i.balance_due)}</td>
                <td className="px-4 py-3 capitalize">{(i.status || "").replace("_", " ")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/invoices/${i.id}`} className="text-teal-700 hover:text-teal-900 font-semibold text-xs">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WorkerDrill({ data }) {
  const { worker, timesheets, summary } = data;
  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniStat label="Timesheets" value={summary.timesheet_count} />
        <MiniStat label="Hours" value={summary.total_hours.toLocaleString()} />
        <MiniStat label="Gross pay" value={money(summary.total_pay)} tone="text-emerald-600" />
        <MiniStat label="Gross bill / margin" value={`${money(summary.total_bill)} / ${money(summary.margin)}`} />
      </div>
      <div className="text-xs text-slate-500 mb-3">{worker.email} · {worker.status}</div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Week</th>
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-right px-4 py-3">Hrs</th>
              <th className="text-right px-4 py-3">PD days</th>
              <th className="text-right px-4 py-3">Pay rate</th>
              <th className="text-right px-4 py-3">Gross pay</th>
              <th className="text-right px-4 py-3">Gross bill</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {timesheets.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No timesheets in this range.</td></tr>}
            {timesheets.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono">{t.week_start}</td>
                <td className="px-4 py-3">{t.project_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{t.client_name}</td>
                <td className="px-4 py-3 text-right font-mono">{t.hours}</td>
                <td className="px-4 py-3 text-right font-mono">{t.per_diem_days}</td>
                <td className="px-4 py-3 text-right font-mono">{money(t.pay_rate)}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-600">{money(t.gross_pay)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(t.gross_bill)}</td>
                <td className="px-4 py-3 capitalize text-xs">{t.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectDrill({ data }) {
  const { project, timesheets, invoices, summary } = data;
  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniStat label="Total billed" value={money(summary.billed)} />
        <MiniStat label="Outstanding" value={money(summary.outstanding)} tone="text-rose-600" />
        <MiniStat label="Margin" value={money(summary.margin)} tone="text-emerald-600" />
        <MiniStat label="Hours" value={summary.total_hours.toLocaleString()} />
      </div>
      <div className="text-xs text-slate-500 mb-3">Client: <b>{project.client_name}</b> · {project.location || "—"} · {project.status}</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="tinylabel mb-2">Timesheets ({timesheets.length})</div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Week</th><th className="text-left px-3 py-2">Worker</th>
                  <th className="text-right px-3 py-2">Hrs</th><th className="text-right px-3 py-2">Bill</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No timesheets.</td></tr>}
                {timesheets.slice(0, 25).map((t) => (
                  <tr key={t.id} className="border-t border-slate-100"><td className="px-3 py-2 font-mono">{t.week_start}</td><td className="px-3 py-2">{t.worker}</td><td className="px-3 py-2 text-right font-mono">{t.hours}</td><td className="px-3 py-2 text-right font-mono">{money(t.gross_bill)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="tinylabel mb-2">Invoices ({summary.invoice_count})</div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Invoice</th><th className="text-left px-3 py-2">Week</th>
                  <th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No invoices.</td></tr>}
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono">
                      <Link to={`/invoices/${i.id}`} className="text-teal-700 hover:text-teal-900">{i.number}</Link>
                    </td>
                    <td className="px-3 py-2">{shortDate(i.week_ending)}</td>
                    <td className="px-3 py-2 text-right font-mono">{money(i.total)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${i.balance_due > 0 ? "text-rose-600" : "text-slate-400"}`}>{money(i.balance_due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`text-lg font-black mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}
